import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calculator, Activity, Layers, Beaker, Droplets, Brush } from "lucide-react";

const STAGE_ICONS = {
  site_cleaning: Layers,
  fill_layers: Beaker,
  road_base: Activity,
  asphalt_coating: Calculator,
  prime_coat: Droplets,
  base_asphalt: Layers,
  tack_coat_1: Droplets,
  binder_course: Brush,
  tack_coat_2: Droplets,
  wearing_course: Brush,
};

const LIQUID_LAYERS = new Set(["prime_coat", "tack_coat_1", "tack_coat_2"]);
const ASPHALT_LAYERS = new Set(["base_asphalt", "binder_course", "wearing_course"]);
const NEEDS_THICKNESS = new Set([
  "fill_layers", "road_base", "asphalt_coating",
  "base_asphalt", "binder_course", "wearing_course",
]);

export default function StageCalculator({ value, onChange, onResult, lengthReadOnly = false }) {
  const { t } = useI18n();
  const [norms, setNorms] = useState([]);
  const [asphaltTypes, setAsphaltTypes] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/norms").then((r) => setNorms(r.data)).catch(() => {});
    api.get("/asphalt-types").then((r) => setAsphaltTypes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const needsLiquid = LIQUID_LAYERS.has(value.stage_type);
    const needsThickness = NEEDS_THICKNESS.has(value.stage_type);
    if (
      !value.stage_type ||
      !(value.length > 0) ||
      !(value.width > 0) ||
      (needsThickness && !(value.thickness > 0)) ||
      (needsLiquid && !(value.consumption_kg_m2 > 0))
    ) {
      setResult(null);
      return;
    }
    const t1 = setTimeout(() => {
      api
        .post("/stages/calc", {
          stage_type: value.stage_type,
          length: Number(value.length),
          width: Number(value.width),
          thickness: value.thickness ? Number(value.thickness) : null,
          k_factor: Number(value.k_factor || 1.25),
          density: Number(value.density || 2.35),
          norm_id: value.norm_id || null,
          consumption_kg_m2: value.consumption_kg_m2
            ? Number(value.consumption_kg_m2) : null,
          asphalt_type_id: value.asphalt_type_id || null,
          asphalt_type_label: value.asphalt_type_label || null,
          loss_pct: value.loss_pct != null && value.loss_pct !== ""
            ? Number(value.loss_pct) : 0.02,
        })
        .then((r) => {
          setResult(r.data);
          setError(null);
          onResult?.(r.data);
        })
        .catch((e) => {
          setError(e?.response?.data?.detail || "Hesablama xətası");
          setResult(null);
        });
    }, 250);
    return () => clearTimeout(t1);
  }, [value, onResult]);

  const StageIcon = STAGE_ICONS[value.stage_type] || Calculator;
  const filteredNorms = norms.filter((n) => n.stage_type === value.stage_type);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Inputs */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <StageIcon className="w-5 h-5 text-[#004D99]" />
          <h3 className="font-heading font-bold text-lg">Texniki Parametrlər</h3>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="label-overline">Mərhələ Tipi</Label>
            <Select
              value={value.stage_type}
              onValueChange={(v) => onChange({ ...value, stage_type: v, norm_id: "" })}
            >
              <SelectTrigger data-testid="stage-type-select">
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t("earthwork_group")}</SelectLabel>
                  <SelectItem value="site_cleaning">{t("site_cleaning")}</SelectItem>
                  <SelectItem value="fill_layers">{t("fill_layers")}</SelectItem>
                  <SelectItem value="road_base">{t("road_base")}</SelectItem>
                  <SelectItem value="asphalt_coating">{t("asphalt_coating")}</SelectItem>
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>{t("pavement_layers_group")}</SelectLabel>
                  <SelectItem value="prime_coat">{t("prime_coat")}</SelectItem>
                  <SelectItem value="base_asphalt">{t("base_asphalt")}</SelectItem>
                  <SelectItem value="tack_coat_1">{t("tack_coat_1")}</SelectItem>
                  <SelectItem value="binder_course">{t("binder_course")}</SelectItem>
                  <SelectItem value="tack_coat_2">{t("tack_coat_2")}</SelectItem>
                  <SelectItem value="wearing_course">{t("wearing_course")}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {filteredNorms.length > 0 && (
            <div>
              <Label className="label-overline">DESN Norma</Label>
              <Select
                value={value.norm_id || ""}
                onValueChange={(v) => onChange({ ...value, norm_id: v })}
              >
                <SelectTrigger data-testid="norm-select">
                  <SelectValue placeholder="Avtomatik" />
                </SelectTrigger>
                <SelectContent>
                  {filteredNorms.map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.table_code} — {n.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-overline">L (m)</Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={value.length}
                onChange={(e) => onChange({ ...value, length: e.target.value })}
                placeholder="0"
                data-testid="input-length"
                readOnly={lengthReadOnly}
                disabled={lengthReadOnly}
                title={lengthReadOnly ? "Uzunluq km aralığından avtomatik hesablanır" : undefined}
                className={lengthReadOnly ? "bg-slate-100 text-slate-600 cursor-not-allowed" : undefined}
              />
              {lengthReadOnly && (
                <div className="text-[10px] mono text-slate-500 mt-1">
                  km aralığından avto-hesablanır
                </div>
              )}
            </div>
            <div>
              <Label className="label-overline">W (m)</Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={value.width}
                onChange={(e) => onChange({ ...value, width: e.target.value })}
                placeholder="0"
                data-testid="input-width"
              />
            </div>
            <div>
              <Label className="label-overline">T (sm)</Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={value.thickness}
                onChange={(e) =>
                  onChange({ ...value, thickness: e.target.value })
                }
                placeholder="0"
                disabled={!NEEDS_THICKNESS.has(value.stage_type)}
                data-testid="input-thickness"
              />
            </div>
          </div>

          {value.stage_type === "fill_layers" && (
            <div>
              <Label className="label-overline">{t("k_factor")}</Label>
              <Input
                type="number"
                step="0.01"
                value={value.k_factor}
                onChange={(e) => onChange({ ...value, k_factor: e.target.value })}
                data-testid="input-k"
              />
            </div>
          )}
          {LIQUID_LAYERS.has(value.stage_type) && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <Label className="label-overline">{t("consumption_kg_m2")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={value.consumption_kg_m2 || ""}
                onChange={(e) =>
                  onChange({ ...value, consumption_kg_m2: e.target.value })
                }
                placeholder={
                  value.stage_type === "prime_coat" ? "0.6 — 1.0" : "0.3 — 0.5"
                }
                data-testid="input-consumption"
              />
              <div className="text-[10px] mono text-slate-500 mt-1">
                {value.stage_type === "prime_coat"
                  ? "Tipik diapazon: 0.6 — 1.0 kg/m² (EBK-1)"
                  : "Tipik diapazon: 0.3 — 0.5 kg/m² (EBK-2)"}
              </div>
            </div>
          )}
          {ASPHALT_LAYERS.has(value.stage_type) && (
            <div className="space-y-3 bg-orange-50 border border-orange-200 rounded-md p-3">
              <div>
                <Label className="label-overline">{t("asphalt_type")}</Label>
                <Select
                  value={value.asphalt_type_id || ""}
                  onValueChange={(v) => {
                    const at = asphaltTypes.find((a) => a.id === v);
                    onChange({
                      ...value,
                      asphalt_type_id: v,
                      asphalt_type_label: at?.label || null,
                      density: at?.density || value.density,
                    });
                  }}
                >
                  <SelectTrigger data-testid="asphalt-type-select">
                    <SelectValue placeholder="Asfalt növünü seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {asphaltTypes
                      .filter((a) => a.layer_name === value.stage_type)
                      .map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.label} · ρ={a.density} t/m³
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline">{t("density")}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={value.density || 2.35}
                    onChange={(e) => onChange({ ...value, density: e.target.value })}
                    data-testid="input-density"
                    readOnly={!!value.asphalt_type_id}
                    className={value.asphalt_type_id ? "bg-slate-100" : undefined}
                  />
                </div>
                <div>
                  <Label className="label-overline">{t("loss_pct_label")}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={value.loss_pct != null ? value.loss_pct : 0.02}
                    onChange={(e) => onChange({ ...value, loss_pct: e.target.value })}
                    data-testid="input-loss"
                    placeholder="0.02"
                  />
                </div>
              </div>
              <div className="text-[10px] mono text-slate-500">
                Tonaj = S × (T/100) × ρ × (1 + itki)
              </div>
            </div>
          )}
          {value.stage_type === "asphalt_coating" && (
            <div>
              <Label className="label-overline">{t("density")}</Label>
              <Input
                type="number"
                step="0.01"
                value={value.density}
                onChange={(e) => onChange({ ...value, density: e.target.value })}
                data-testid="input-density-legacy"
              />
            </div>
          )}
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-slate-900 text-white rounded-lg p-6 grid-paper">
        <div className="label-overline text-slate-400 mb-3">{t("live_preview")}</div>
        {error && <div className="text-red-400 text-sm mono">{error}</div>}
        {!result && !error && (
          <div className="text-slate-500 text-sm">
            Parametrləri daxil edin → nəticə avtomatik göstəriləcək
          </div>
        )}
        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {result.area_m2 != null && (
                <div className="bg-slate-800 rounded p-3">
                  <div className="label-overline text-slate-400">{t("area")}</div>
                  <div className="text-2xl mono font-bold mt-1" data-testid="preview-area">
                    {result.area_m2.toLocaleString()} m²
                  </div>
                </div>
              )}
              {result.volume_m3 != null && (
                <div className="bg-slate-800 rounded p-3">
                  <div className="label-overline text-slate-400">{t("volume")}</div>
                  <div className="text-2xl mono font-bold mt-1" data-testid="preview-volume">
                    {result.volume_m3.toLocaleString()} m³
                  </div>
                </div>
              )}
              {result.tonnage_t != null && (
                <div className="bg-slate-800 rounded p-3">
                  <div className="label-overline text-slate-400">{t("tonnage")}</div>
                  <div className="text-2xl mono font-bold mt-1" data-testid="preview-tonnage">
                    {result.tonnage_t.toLocaleString()} t
                  </div>
                </div>
              )}
              {result.emulsion_tons != null && (
                <div className="bg-blue-900/40 rounded p-3 border border-blue-700/40">
                  <div className="label-overline text-blue-300">
                    {t("emulsion_tons")}
                  </div>
                  <div className="text-2xl mono font-bold mt-1" data-testid="preview-emulsion">
                    {result.emulsion_tons.toLocaleString()} t
                  </div>
                  <div className="text-[10px] mono text-blue-300/60 mt-1">
                    {result.consumption_kg_m2} kg/m²
                  </div>
                </div>
              )}
              {result.coef != null && (
                <div className="bg-slate-800 rounded p-3">
                  <div className="label-overline text-slate-400">Əmsal</div>
                  <div className="text-2xl mono font-bold mt-1">
                    {result.coef.toLocaleString()}
                  </div>
                </div>
              )}
            </div>

            {result.resources && (
              <div className="space-y-2 pt-2 border-t border-slate-700">
                {[
                  ["workers", t("workers")],
                  ["machinery", t("machinery")],
                  ["materials", t("materials")],
                ].map(([k, label]) => {
                  const arr = result.resources[k];
                  if (!arr || !arr.length) return null;
                  return (
                    <div key={k}>
                      <div className="label-overline text-slate-400 mb-1">{label}</div>
                      <div className="space-y-1">
                        {arr.map((r, i) => (
                          <div
                            key={i}
                            className="flex justify-between text-xs mono"
                          >
                            <span className="text-slate-300 truncate">{r.name}</span>
                            <span className="text-white">
                              {r.total} {r.unit}
                              {r.cost > 0 && ` · ${r.cost.toFixed(0)}₼`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {result.total_cost > 0 && (
              <div className="pt-3 border-t border-slate-700 flex justify-between items-baseline">
                <div className="label-overline text-slate-400">{t("total_cost")}</div>
                <div className="text-3xl mono font-bold text-[#FF5722]" data-testid="preview-total-cost">
                  {result.total_cost.toLocaleString()} ₼
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
